import {IServiceFactory} from "../service-factory.interface";
import {IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {IStorage} from "./storage.interface";
import {ILargeCacheHandlerEventSubscription, LargeCacheHandlerOptions} from "./large-cache.handler.interface";
import {Debounce} from "../../utils/debounce";
import {TimeSpan} from "../../types/time-span";

export interface ILargeCacheData {
    timestamp: number;
}

export class LargeCacheHandler<TData extends ILargeCacheData, TStorageKeys extends string> {

    constructor(private readonly options: LargeCacheHandlerOptions<TData, TStorageKeys>) {

        this._saveCacheDebounce = new Debounce(TimeSpan.fromMilliseconds(100));
        this._cacheData = this.storage.getJson<Record<string, TData>>(options.storageKey) ?? {};

        this._clearExpiredAndTrimCache();

        /*
        if(this.options.resetOnAuthorizationChanged) {
            this._reactions.push(reaction(() => this.services.user.profile,
                () => {
                    this.resetCache();
                }));
        }
         */

       this._reactions.push(reaction(() => this.services.application.isActive,
            (isActive) => {
                if(isActive) {
                    this._clearExpiredAndTrimCache();
                }
            }));

        this._intervalsRefs.push(setInterval(() => {
            this._clearExpiredAndTrimCache();
        }, this.options.trimInterval?.totalMilliseconds ?? 60000));

        makeObservable<this, '_cacheData'>(this, {
            _cacheData: observable,
        });
    }

    private readonly _saveCacheDebounce: Debounce;

    private get services(): IServiceFactory {
        return this.options.services;
    }

    private get storage(): IStorage<TStorageKeys> {
        return this.options.storage;
    }


    private _cacheData: Record<string, TData> = {};

    private _reactions: IReactionDisposer[] = [];
    private _intervalsRefs: any[] = [];


    dispose(): void {
        this._saveCacheDebounce.dispose();
        this._reactions.forEach(r => r());
        this._reactions = [];
        this._intervalsRefs.forEach(interval => clearInterval(interval));
        this._intervalsRefs = [];
        this.resetCache();
        this._onCacheItemRemovedSubscriptions = [];

    }


    getItem(key: string): TData | undefined {
        return this._cacheData[key];
    }

    setItem(key: string, value: TData): void {
        runInAction(() => {
            this._cacheData[key] = value;
        });

        this.saveCache({saveAsync: true});
    }

    removeItem(key: string): void {
        runInAction(() => {
            delete this._cacheData[key];
        });

        this.saveCache({saveAsync: true});
    }

    isTimestampExpired(timestamp: number): boolean {
        return timestamp < Date.now() - this.options.cacheExpirationTime.totalMilliseconds;
    }


    saveCache(options: {saveAsync: boolean}): void {


        const executeSave = () => {

            try {
                this.storage.setJson(this.options.storageKey, this._cacheData);
            } catch (err) {
                this.services.logger.error(`Failed to save ${this.options.name} cache`, err);
                this.resetCache();
            }
        }

        if(options.saveAsync) {
            this._saveCacheDebounce.execute(executeSave);
        } else {
            executeSave();
        }
    }

    private _onCacheItemRemovedSubscriptions: Array<(key: string) => void> = [];
    onCacheItemRemoved(callback: (key: string) => void): ILargeCacheHandlerEventSubscription {
        this._onCacheItemRemovedSubscriptions.push(callback);
        return {
            unsubscribe: () => {
                const index = this._onCacheItemRemovedSubscriptions.indexOf(callback);
                if(index >= 0) {
                    this._onCacheItemRemovedSubscriptions.splice(index, 1);
                }
            }
        }
    }

    resetCache(): void {
        this.storage.removeItem(this.options.storageKey);
        runInAction(() => {
            const keys = Object.keys(this._cacheData);
            this._cacheData = {};
            this._fireCacheItemsRemoved(keys);
        });
    }

    private _fireCacheItemsRemoved(keys: string[]): void {
        keys.forEach(key => {
            this._onCacheItemRemovedSubscriptions.forEach(callback => {
                try {
                    callback(key)
                } catch (err) {
                    this.services.logger.error(`Failed to notify cache item removed for key ${key}`, err);
                }
            });
        })

    }

    private _clearExpiredAndTrimCache(): void {
        this._clearExpired();
        this._trimCache();
    }

    private _clearExpired(): void {
        const removedKeys: string[] = [];
        runInAction(() => {
            for(let cacheKey of Object.keys(this._cacheData)) {
                const cacheData = this._cacheData[cacheKey];

                if(this.isTimestampExpired(cacheData.timestamp)) {
                    removedKeys.push(cacheKey);
                    delete this._cacheData[cacheKey];
                }
            }
        })

        if(removedKeys.length > 0) {
            this.saveCache({saveAsync: false});

            this._fireCacheItemsRemoved(removedKeys);
        }
    }

    private _trimCache(): void {

        const keys = Object.keys(this._cacheData);
        if (keys.length <= this.options.maxItems) {
            return;
        }

        keys.sort((a, b) => this._cacheData[a].timestamp - this._cacheData[b].timestamp);
        const removedKeys: string[] = [];
        runInAction(() => {
            for (let i = 0; i < keys.length - this.options.maxItems; i++) {
                delete this._cacheData[keys[i]];
                removedKeys.push(keys[i]);
            }
        })

        this.saveCache({saveAsync: false});

        this._fireCacheItemsRemoved(removedKeys);

    }


}